import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { lookupValuesAPI } from '../services/api';
import { Briefcase, Users, Plus, X, Loader2, ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useNavigate } from 'react-router-dom';

const CustomerSettings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Lookup values state (Industries & Referral Sources)
  const [industries, setIndustries] = useState([]);
  const [referralSources, setReferralSources] = useState([]);
  const [newIndustry, setNewIndustry] = useState('');
  const [newReferralSource, setNewReferralSource] = useState('');
  const [addIndustryDialog, setAddIndustryDialog] = useState(false);
  const [addReferralSourceDialog, setAddReferralSourceDialog] = useState(false);
  const [loadingLookups, setLoadingLookups] = useState(true);

  useEffect(() => {
    fetchLookupValues();
  }, []);

  const fetchLookupValues = async () => {
    try {
      setLoadingLookups(true);
      const response = await lookupValuesAPI.getAll();
      setIndustries(response.data.industries || []);
      setReferralSources(response.data.referralSources || []);
    } catch (error) {
      console.error('Error fetching lookup values:', error);
    } finally {
      setLoadingLookups(false);
    }
  };

  const handleAddIndustry = async () => {
    if (!newIndustry.trim()) return;
    try {
      await lookupValuesAPI.addIndustry(newIndustry.trim());
      setIndustries(prev => [...prev, newIndustry.trim()]);
      setNewIndustry('');
      setAddIndustryDialog(false);
      toast({ title: 'Success', description: 'Industry added successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to add industry', variant: 'destructive' });
    }
  };

  const handleDeleteIndustry = async (value) => {
    try {
      await lookupValuesAPI.deleteIndustry(value);
      setIndustries(prev => prev.filter(i => i !== value));
      toast({ title: 'Success', description: 'Industry deleted successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete industry', variant: 'destructive' });
    }
  };

  const handleAddReferralSource = async () => {
    if (!newReferralSource.trim()) return;
    try {
      await lookupValuesAPI.addReferralSource(newReferralSource.trim());
      setReferralSources(prev => [...prev, newReferralSource.trim()]);
      setNewReferralSource('');
      setAddReferralSourceDialog(false);
      toast({ title: 'Success', description: 'Referral source added successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to add referral source', variant: 'destructive' });
    }
  };

  const handleDeleteReferralSource = async (value) => {
    try {
      await lookupValuesAPI.deleteReferralSource(value);
      setReferralSources(prev => prev.filter(r => r !== value));
      toast({ title: 'Success', description: 'Referral source deleted successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete referral source', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/settings')} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Button>
      </div>
      
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Customer Settings</h1>
        <p className="text-gray-500 mt-1">Manage industries and referral sources for clients</p>
      </div>

      {/* Industries Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-blue-600" />
              Industries
            </div>
            <Button size="sm" onClick={() => setAddIndustryDialog(true)} data-testid="add-industry-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Industry
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-4">
            Define the industries your clients operate in. These values appear in client profiles and can be used for filtering and reporting.
          </p>
          {loadingLookups ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {industries.map((industry) => (
                <div
                  key={industry}
                  className="flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-lg text-sm border border-blue-200"
                >
                  {industry}
                  <button
                    onClick={() => handleDeleteIndustry(industry)}
                    className="ml-1 hover:text-red-500 transition-colors"
                    data-testid={`delete-industry-${industry}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {industries.length === 0 && (
                <p className="text-gray-400 text-sm py-4">No industries added yet. Click "Add Industry" to create one.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Referral Sources Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-green-600" />
              Referral Sources
            </div>
            <Button size="sm" onClick={() => setAddReferralSourceDialog(true)} data-testid="add-referral-source-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Referral Source
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-4">
            Track how clients found your firm. These values appear in client profiles and help analyze marketing effectiveness.
          </p>
          {loadingLookups ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {referralSources.map((source) => (
                <div
                  key={source}
                  className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg text-sm border border-green-200"
                >
                  {source}
                  <button
                    onClick={() => handleDeleteReferralSource(source)}
                    className="ml-1 hover:text-red-500 transition-colors"
                    data-testid={`delete-referral-source-${source}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {referralSources.length === 0 && (
                <p className="text-gray-400 text-sm py-4">No referral sources added yet. Click "Add Referral Source" to create one.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Industry Dialog */}
      <Dialog open={addIndustryDialog} onOpenChange={setAddIndustryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Industry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Industry Name</Label>
              <Input
                value={newIndustry}
                onChange={(e) => setNewIndustry(e.target.value)}
                placeholder="e.g., Healthcare, Technology, Retail"
                data-testid="new-industry-input"
                onKeyDown={(e) => e.key === 'Enter' && handleAddIndustry()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddIndustryDialog(false)}>Cancel</Button>
            <Button onClick={handleAddIndustry} data-testid="save-industry-btn">Add Industry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Referral Source Dialog */}
      <Dialog open={addReferralSourceDialog} onOpenChange={setAddReferralSourceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Referral Source</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Referral Source Name</Label>
              <Input
                value={newReferralSource}
                onChange={(e) => setNewReferralSource(e.target.value)}
                placeholder="e.g., Google, Client Referral, LinkedIn"
                data-testid="new-referral-source-input"
                onKeyDown={(e) => e.key === 'Enter' && handleAddReferralSource()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddReferralSourceDialog(false)}>Cancel</Button>
            <Button onClick={handleAddReferralSource} data-testid="save-referral-source-btn">Add Source</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerSettings;
